// Email Copilot (#/email-copilot, admins) — Atlas's cold email to new
// sellers, run as campaigns (backend app/email_copilot.py, API
// routes/email_copilot.py). The one human decision is Accept on a drafted
// plan; Go live, Pause, Resume and Stop are buttons here, each confirmed.
// The chat on the right is Ask Atlas with page context 'email_copilot'
// (app/assistant/copilot_tools.py): it reads the numbers and can draft or
// revise a plan, never change anything that runs.
//
//   window.EmailCopilotView   the page (app.jsx)

const CP_API = '/api/email-copilot';
const cpGet = (p) => window.AtlasApi.fetch(CP_API + p, { cacheMs: 0 });
const cpSend = (p, method, body) =>
  window.AtlasApi.fetch(CP_API + p, { method, body: body != null ? JSON.stringify(body) : undefined });

const CP_LABEL = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-4)', fontWeight: 600 };
const CP_CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16 };
const CP_FUNNEL = [
  ['picked', 'Picked'], ['qualified', 'Qualified'], ['would_send', 'Would send'], ['enrolled', 'Enrolled'], ['sent', 'Emailed'],
  ['replied', 'Replied'], ['positive', 'Positive'], ['booked', 'Booked'],
];
// "Would send" is shadow's step: qualified owners whose emails passed the pre-send check.
const cpShadow = (c) => !!c && c.mode !== 'live' && c.status !== 'draft';
const cpFunnelSteps = (shadow) => CP_FUNNEL.filter(([k]) => shadow || k !== 'would_send');
const CP_KPIS = [
  ['picked', 'Picked'], ['qualified', 'Qualified'], ['enrolled', 'Enrolled'], ['emails', 'Emails sent'],
  ['replied', 'Replies'], ['positive', 'Positive'], ['booked', 'Booked'], ['bounced', 'Bounced'],
];
const CP_STATUS = {
  draft: ['Proposed', 'violet'], running: ['Running', 'green'], paused: ['Paused', 'amber'],
  done: ['Done', 'slate'], stopped: ['Stopped', 'slate'],
};
const CP_LEAD_STATUS = {
  picked: ['Picked', 'slate'], qualified: ['Qualified', 'blue'], enrolling: ['Enrolling', 'blue'],
  enrolled: ['Enrolled', 'green'], held: ['Held', 'amber'], skipped: ['Skipped', 'slate'],
  failed: ['Failed', 'rose'], removed: ['Removed', 'slate'],
};
const CP_BOX_STATUS = { ramping: ['Ramping', 'blue'], live: ['Live', 'green'], paused: ['Paused', 'rose'], unmanaged: ['Not managed', 'slate'] };

const cpNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
const cpPct = (x, digits = 1) => (x == null ? '—' : `${(x * 100).toFixed(digits)}%`);
const cpWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
};
const cpDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—');
const cpPlural = (n, one, many) => `${cpNum(n)} ${n === 1 ? one : (many || one + 's')}`;
const cpPerson = (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || '—';

// Every action that changes what runs asks first.
const CP_CONFIRM = {
  accept: (c) => ({ title: `Accept “${c.name}”?`, body: 'It starts picking and qualifying owners right away.', confirmLabel: 'Accept' }),
  live: (c) => ({ title: `Go live with “${c.name}”?`, body: 'Its owners start getting emails.', confirmLabel: 'Go live', danger: true }),
  pause: (c) => ({ title: `Pause “${c.name}”?`, body: 'Its emails stop; people mid-sequence wait.', confirmLabel: 'Pause' }),
  resume: (c) => ({ title: `Resume “${c.name}”?`, body: 'Its emails start again.', confirmLabel: 'Resume' }),
  stop: (c) => ({ title: `Stop “${c.name}”?`, body: c.status === 'draft' ? 'The plan is set aside.' : 'This ends the campaign for good.', confirmLabel: c.status === 'draft' ? 'Discard' : 'Stop', danger: true }),
  'pause-all': () => ({ title: 'Pause every campaign?', body: 'Every running campaign stops sending until you resume.', confirmLabel: 'Pause all', danger: true }),
  'resume-all': () => ({ title: 'Resume?', body: 'The campaigns Pause all stopped start sending again.', confirmLabel: 'Resume' }),
};
const cpConfirm = (action, c) => window.showConfirm(CP_CONFIRM[action](c || {}));

const cpToastError = (title, e) => window.pushToast({ type: 'error', title, body: (e && e.message) || '' });

// ── Small pieces ─────────────────────────────────────────────────────────────

const CpStatus = ({ c }) => {
  const [label, hue] = CP_STATUS[c.status] || [c.status, 'slate'];
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      <window.Pill hue={hue} dot sz="sm">{label}</window.Pill>
      {(c.status === 'running' || c.status === 'paused') && (
        <window.Pill hue={c.mode === 'live' ? 'teal' : 'slate'} sz="sm" title={c.mode === 'live' ? 'Sending' : 'Picks and qualifies, sends nothing'}>
          {c.mode === 'live' ? 'Live' : 'Shadow'}
        </window.Pill>
      )}
    </span>
  );
};

// A narrow drawer scrolls the tabs sideways instead of widening every tab's
// content past the screen; the rule underneath is a shadow so the scroll box
// doesn't clip the active tab's underline.
const CpTabs = ({ tabs, tab, onTab }) => (
  <div style={{ display: 'flex', gap: 2, boxShadow: 'inset 0 -1px 0 var(--border)', overflowX: 'auto', overflowY: 'hidden' }}>
    {tabs.map(([k, label, n]) => (
      <button key={k} type="button" onClick={() => onTab(k)}
        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '8px 10px', fontSize: 12.5,
                 fontWeight: tab === k ? 600 : 500, color: tab === k ? 'var(--text)' : 'var(--text-3)', flexShrink: 0, whiteSpace: 'nowrap',
                 borderBottom: `2px solid ${tab === k ? 'var(--accent)' : 'transparent'}` }}>
        {label}{n != null && <span style={{ marginLeft: 5, color: 'var(--text-4)', fontWeight: 500 }}>{cpNum(n)}</span>}
      </button>
    ))}
  </div>
);

// picked → … → booked as bars against picked; `compact` for list rows.
const CpFunnel = ({ f, compact, shadow }) => {
  const top = Math.max(1, (f && f.picked) || 0);
  const steps = cpFunnelSteps(shadow);
  if (compact) {
    return (
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-3)' }}>
        {steps.map(([k, label]) => (
          <span key={k}><span style={{ color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{cpNum(f[k])}</span> {label.toLowerCase()}</span>
        ))}
        <span>{cpPct(f.reply_rate)} reply rate</span>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 70px', gap: '6px 10px', alignItems: 'center' }}>
      {steps.map(([k, label]) => (
        <React.Fragment key={k}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{label}</span>
          <div style={{ height: 10, background: 'var(--surface-2, var(--surface-hover))', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${Math.round(((f[k] || 0) / top) * 100)}%`, minWidth: f[k] ? 2 : 0, height: '100%', background: 'var(--accent)', opacity: 0.75 }} />
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{cpNum(f[k])}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

const CpStat = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={CP_LABEL}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 600, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

// Estimates are the plan's own numbers (the brain writes them; keys in
// email_copilot_brain). Known keys get a label; a two-number list is a range;
// spend is the one $ on the page. Text keys read as lines under the numbers.
const CP_COUNTS = ['pool', 'after_exclusions', 'confirmed_email', 'with_owner_email', 'pass_on_rules'];
const CP_EST = [
  ['pool', 'In the pool', 'n'], ['after_exclusions', 'After exclusions', 'n'], ['confirmed_email', 'Confirmed email', 'n'],
  ['with_owner_email', 'Owner email', 'n'], ['pass_on_rules', 'Pass the rules', 'n'],
  ['contacts', 'Contacts', 'n'], ['days', 'Days', 'n'], ['start', 'Starts', 'date'], ['end', 'Ends', 'date'],
  ['reply_rate', 'Reply rate', 'pct'], ['replies', 'Replies', 'n'], ['positive_rate', 'Positive rate', 'pct'],
  ['positive', 'Positive replies', 'n'], ['booked', 'Calls booked', 'n'], ['baseline_positive_rate', 'Positive rate so far', 'pct'],
  ['success', 'Success looks like', 's'], ['usd_per_day', 'Est. spend / day', 'usd'], ['usd_total', 'Est. spend', 'usd'],
];
const CP_EST_TEXT = [['daily_why', 'Volume'], ['expected_basis', 'Expected because']];
const cpEstVal = (v, kind) => {
  const one = (x) => (kind === 'pct' ? cpPct(x) : kind === 'usd' ? `$${Number(x).toFixed(2)}` : kind === 'date' ? cpDate(x)
    : typeof x === 'number' ? cpNum(x) : String(x));
  if (Array.isArray(v)) return v.length === 2 && v.every((x) => typeof x === 'number') ? `${one(v[0])}–${one(v[1])}` : v.map(one).join(', ');
  return one(v);
};
const cpHuman = (k) => k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const CpEstimates = ({ est, only, skip }) => {
  const e = est || {};
  const known = CP_EST.filter(([k]) => e[k] != null && (!only || only.includes(k)) && !(skip || []).includes(k));
  const listed = new Set([...CP_EST.map(([k]) => k), ...CP_EST_TEXT.map(([k]) => k), 'sample', 'recent_sales']);
  const scalar = (v) => v != null && (typeof v !== 'object' || (Array.isArray(v) && v.every((x) => typeof x !== 'object')));
  const extra = only ? [] : Object.keys(e).filter((k) => !listed.has(k) && scalar(e[k]) && !(skip || []).includes(k));
  const text = only ? [] : CP_EST_TEXT.filter(([k]) => e[k] && !(skip || []).includes(k));
  if (!known.length && !extra.length && !text.length) return null;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12 }}>
        {known.map(([k, label, kind]) => <CpStat key={k} label={label} value={cpEstVal(e[k], kind)} />)}
        {extra.map((k) => <CpStat key={k} label={cpHuman(k)} value={cpEstVal(e[k], 'n')} />)}
      </div>
      {text.map(([k, label]) => (
        <div key={k} style={{ fontSize: 12.5, color: 'var(--text-2)' }}><span style={{ color: 'var(--text-4)' }}>{label}: </span>{String(e[k])}</div>
      ))}
    </div>
  );
};

// recent_sales: [{trade, where, when}] — nearby sales the copy may lean on.
const CpRecentSales = ({ rows }) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  return (
    <div>
      <div style={{ ...CP_LABEL, marginBottom: 6 }}>Recent sales</div>
      <div style={{ display: 'grid', gap: 3, fontSize: 12.5, color: 'var(--text-2)' }}>
        {rows.slice(0, 8).map((r, i) => (
          <div key={i}>{[r.trade, r.where].filter(Boolean).join(' · ')}{r.when ? <span style={{ color: 'var(--text-4)' }}> · {r.when}</span> : null}</div>
        ))}
      </div>
    </div>
  );
};

const CpSample = ({ rows }) => {
  if (!Array.isArray(rows) || !rows.length) return null;
  return (
    <div>
      <div style={{ ...CP_LABEL, marginBottom: 6 }}>Sample</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px 16px', fontSize: 12.5 }}>
        {rows.slice(0, 10).map((r, i) => (
          <div key={r.company_id || i} style={{ display: 'flex', gap: 6, minWidth: 0 }}>
            <a href={r.company_id ? `#/companies/${r.company_id}` : undefined}
              style={{ color: r.company_id ? 'var(--accent-text)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.name || r.company || '—'}
            </a>
            <span style={{ color: 'var(--text-4)', whiteSpace: 'nowrap' }}>
              {[r.city, r.state].filter(Boolean).join(', ')}{r.employees ? ` · ${r.employees}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Who calls the owners: PhoneBurner gets each one who hasn't replied on this
// member's seat a day after their last email. Seats are the Calling page's (a member matched to
// a PhoneBurner seat).
const CpCaller = ({ c, pbOwner }) => {
  const [seats, setSeats] = React.useState(null);
  const [value, setValue] = React.useState(c.pb_owner_id || '');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setValue(c.pb_owner_id || ''); }, [c.id, c.pb_owner_id]);
  React.useEffect(() => {
    let live = true;
    window.AtlasApi.dialerStatus()
      .then((s) => { if (live) setSeats(((s && s.members) || []).filter((m) => m.atlas_member_id)); })
      .catch(() => { if (live) setSeats([]); });
    return () => { live = false; };
  }, []);
  if (c.status === 'done' || c.status === 'stopped') return pbOwner || '—';
  const opts = (seats || []).map((m) => ({ value: m.atlas_member_id, label: m.atlas_member_name || m.name }));
  if (value && !opts.some((o) => o.value === value)) opts.unshift({ value, label: pbOwner || 'No seat' });
  const save = async (v) => {
    const prev = value;
    setValue(v); setBusy(true);
    try { await cpSend(`/campaigns/${c.id}/caller`, 'PUT', { pb_owner_id: v || null }); }
    catch (e) { setValue(prev); window.pushToast?.({ type: 'error', title: 'Could not set the caller', body: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <window.Picker value={value} options={opts} emptyLabel="Nobody" size="sm" searchable={opts.length > 8}
      disabled={busy || seats === null} title="PhoneBurner gets each owner who hasn’t replied on this seat, a day after their last email"
      onChange={save} />
  );
};

const CpAudience = ({ c, industries, pbOwner }) => {
  const sp = c.spec || {};
  const parts = [
    ['Industries', (industries || []).map((i) => i.name).join(', ') || cpPlural((sp.industry_ids || []).length, 'industry', 'industries')],
    ['States', (sp.states || []).length ? sp.states.join(', ') : 'Lower 48'],
    ['Quiet for', `${sp.quiet_months || 12} months`],
    ['Per day', cpNum(c.daily_target)],
    ['Up to', sp.max_contacts ? cpNum(sp.max_contacts) : '—'],
    ['Ends', sp.end_date ? cpDate(sp.end_date) : '—'],
    ['Calls after the last email', <CpCaller c={c} pbOwner={pbOwner} />],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
      {parts.map(([k, v]) => <CpStat key={k} label={k} value={<span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>} />)}
    </div>
  );
};

// Figures in the copy, each traced to its source (app/copy_sources.py):
// sourced = our closings / processes, internal = GF Data only, unsourced = none.
const CP_CLAIM = {
  sourced: { color: 'var(--accent-text)', borderBottom: '1px dotted var(--accent)' },
  internal: { color: 'var(--amber-fg)', borderBottom: '1px dotted var(--amber-fg)' },
  unsourced: { color: 'var(--rose-fg)', background: 'var(--rose-bg, rgba(225, 29, 72, 0.08))', borderRadius: 3 },
};
const cpClaimTitle = (c) => (c.source ? `${c.source.label}\n${c.source.detail || ''}`
  : c.why || 'No source in Atlas: our closings or our own processes');
const cpClaimKey = (c) => `${c.step}:${c.part}:${c.start}`;
// One string with its figures marked; clicking a figure opens its source.
const CpMarked = ({ text, marks, onPick }) => {
  const ms = (marks || []).slice().sort((a, b) => a.start - b.start);
  if (!ms.length) return text || '';
  const out = [];
  let at = 0;
  ms.forEach((c) => {
    if (c.start < at) return;
    if (c.start > at) out.push(text.slice(at, c.start));
    out.push(
      <span key={cpClaimKey(c)} role="button" tabIndex={0} data-claim={c.status} title={cpClaimTitle(c)}
        onClick={() => onPick(c)} onKeyDown={(e) => { if (e.key === 'Enter') onPick(c); }}
        style={{ ...CP_CLAIM[c.status], cursor: 'pointer', padding: c.status === 'unsourced' ? '0 2px' : 0 }}>
        {text.slice(c.start, c.end)}
      </span>,
    );
    at = c.end;
  });
  if (at < text.length) out.push(text.slice(at));
  return out;
};
const CpClaimCard = ({ c, onClose }) => (
  <div style={{ marginTop: 6, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2, var(--surface-hover))', fontSize: 12 }}>
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <span style={{ fontWeight: 600, ...CP_CLAIM[c.status], background: 'none' }}>{c.text}</span>
      <window.Pill hue={{ sourced: 'blue', internal: 'amber', unsourced: 'rose' }[c.status]} sz="sm">
        {{ sourced: 'Sourced', internal: 'Internal only', unsourced: 'No source' }[c.status]}
      </window.Pill>
      <button type="button" onClick={onClose} aria-label="Close"
        style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-4)' }}>×</button>
    </div>
    {c.source ? (
      <div style={{ marginTop: 4, lineHeight: 1.5 }}>
        <div style={{ color: 'var(--text)' }}>{c.source.label}</div>
        <div style={{ color: 'var(--text-3)' }}>{c.source.detail}</div>
      </div>
    ) : <div style={{ marginTop: 4, color: 'var(--text-3)' }}>{c.why || 'Nothing in our closings or our own processes backs this figure.'}</div>}
  </div>
);

const CpSteps = ({ steps, claims }) => {
  const [open, setOpen] = React.useState(null);
  const marks = (step, part) => (claims || []).filter((c) => c.step === step && c.part === part);
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {(steps || []).slice().sort((a, b) => (a.step || 0) - (b.step || 0)).map((s) => {
        const pick = (c) => setOpen((o) => (o && cpClaimKey(o) === cpClaimKey(c) ? null : c));
        return (
          <div key={s.step} style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
              Email {s.step}{s.step > 1 ? ` · ${cpPlural(s.delay_days || 0, 'day')} later` : ''}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}><CpMarked text={s.subject} marks={marks(s.step, 'subject')} onPick={pick} /></div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'pre-wrap', marginTop: 3, lineHeight: 1.5 }}>
              <CpMarked text={s.body} marks={marks(s.step, 'body')} onPick={pick} />
            </div>
            {open && open.step === s.step && <CpClaimCard c={open} onClose={() => setOpen(null)} />}
          </div>
        );
      })}
    </div>
  );
};

// A variant folds to its header like a record page section, remembered per
// viewer (atlas.section.copilot.variant.<id>); a retired one starts folded.
const cpVariantSection = (id) => `copilot.variant.${id}`;
const cpVariantPreview = (steps) => {
  const ss = (steps || []).slice().sort((a, b) => (a.step || 0) - (b.step || 0));
  const days = ss.reduce((n, s) => n + (Number(s.delay_days) || 0), 0);
  return [`${cpPlural(ss.length, 'email')}${days ? ` over ${cpPlural(days, 'day')}` : ''}`,
          ss.map((s) => s.subject).filter(Boolean).join(' → ')].filter(Boolean).join(' · ');
};
// Live variants first (the ones being tested), retired after, each in its own order.
const cpVariantOrder = (vs) => [...vs.filter((v) => v.status !== 'retired'), ...vs.filter((v) => v.status === 'retired')];
// Expand all / Collapse all over one list of variants, not the rest of the page.
const cpFoldVariants = (vs, open) => window.dispatchEvent(new CustomEvent('atlas-sections',
  { detail: { open, ids: vs.map((v) => cpVariantSection(v.id)) } }));

// `owners`: every owner shadow has a preview for, across the variants — each
// gets one variant, so a card shows its share ("25 of 50 owners").
const CpVariant = ({ v, showResults, shadow, owners }) => {
  const r = v.results || {};
  const retired = v.status === 'retired';
  const unsourced = (v.claims || []).filter((c) => c.status !== 'sourced').length;
  const head = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {retired && <window.Pill hue="slate" sz="sm" title={v.retire_reason || ''}>Retired</window.Pill>}
      {unsourced > 0 && (
        <window.Pill hue="rose" sz="sm" title="The pre-send check holds copy with a figure Atlas can't trace">
          {cpPlural(unsourced, 'figure')} without a usable source
        </window.Pill>
      )}
      {v.author === 'model' && <window.Pill hue="violet" sz="sm" title="Written by the Email Copilot, not a person">Copilot</window.Pill>}
      {showResults && shadow && !retired && (
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}
          title="Owners whose emails would be this variant's when the campaign goes live">
          {owners ? `${cpNum(r.would_send)} of ${cpPlural(owners, 'owner')}` : cpPlural(r.would_send || 0, 'owner')}
        </span>
      )}
      {showResults && !shadow && (
        <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
          {cpNum(r.sent)} emailed · {cpNum(r.replied)} replied ({cpPct(r.reply_rate)}) · {cpNum(r.positive)} positive · {cpNum(r.booked)} booked
          {r.score != null && <span title="Positive 2 points, any other reply that isn't a no 1, over owners whose sequence has played out"> · score {Number(r.score).toFixed(3)} on {cpNum(r.matured)}</span>}
        </span>
      )}
    </span>
  );
  return (
    <window.Section collapsible id={cpVariantSection(v.id)} defaultOpen={!retired} title={v.label}
      preview={cpVariantPreview(v.steps)} action={head} style={retired ? { opacity: 0.65 } : undefined}>
      <div style={{ padding: 12 }}>
        {v.rationale && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 4, lineHeight: 1.5 }}>{v.rationale}</div>}
        {v.retire_reason && <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Retired: {v.retire_reason}</div>}
        <div style={{ marginTop: v.rationale || v.retire_reason ? 6 : 0 }}><CpSteps steps={v.steps} claims={v.claims} /></div>
        {!retired && <div style={{ marginTop: 10 }}><CpSendTest variantId={v.id} /></div>}
      </div>
    </window.Section>
  );
};

const CpJournal = ({ rows }) => {
  if (!rows || !rows.length) return <div style={{ fontSize: 12.5, color: 'var(--text-4)', padding: '8px 0' }}>Nothing yet.</div>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((j) => (
        <div key={j.id} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{cpWhen(j.created_at)}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text)' }}>
              {j.summary}
              {j.actor === 'user' && <span style={{ color: 'var(--text-4)' }}> · by hand</span>}
            </div>
            {j.reasoning && <window.Markdown style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{j.reasoning}</window.Markdown>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Proposed plans ───────────────────────────────────────────────────────────

const CpPlanCard = ({ c, onChanged, onAsk }) => {
  const [d, setD] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    cpGet(`/campaigns/${c.id}`).then((x) => { if (live) setD(x); }).catch((e) => { if (live) setD({ error: e.message }); });
    return () => { live = false; };
  }, [c.id, c.version]);
  const act = async (action) => {
    if (!(await cpConfirm(action, c))) return;
    setBusy(action);
    try {
      await cpSend(`/campaigns/${c.id}/${action}`, 'POST', action === 'stop' ? { reason: 'plan discarded' } : null);
      window.pushToast({ type: 'success', title: action === 'accept' ? `Accepted “${c.name}”` : `Discarded “${c.name}”` });
      onChanged();
    } catch (e) { cpToastError(action === 'accept' ? "Couldn't accept" : "Couldn't discard", e); }
    finally { setBusy(null); }
  };
  const problems = (d && d.problems) || [];
  const est = c.estimates || {};
  return (
    <div data-plan-card={c.id} style={{ ...CP_CARD, display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{c.name}</span>
            <CpStatus c={c} />
            {c.proposal && <window.Pill hue="slate" sz="sm" title={c.proposal.why || ''}>Copilot's pick</window.Pill>}
            {c.version > 1 && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>v{c.version}</span>}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 2 }}>{cpWhen(c.created_at)}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <window.Btn size="sm" variant="ghost" disabled={!!busy} onClick={() => act('stop')}>Discard</window.Btn>
          <window.Btn size="sm" variant="outline" onClick={() => onAsk(c, `Change the “${c.name}” plan: `)}>Ask for changes</window.Btn>
          <window.Btn size="sm" variant="accent" disabled={!!busy || !d || !!d.error || problems.length > 0}
            title={problems.length ? 'Fix what blocks it first' : undefined} onClick={() => act('accept')}>
            {busy === 'accept' ? 'Accepting…' : 'Accept'}
          </window.Btn>
        </div>
      </div>
      {problems.length > 0 && (
        <div style={{ background: 'var(--rose-bg, var(--surface-2))', color: 'var(--rose-fg)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5 }}>
          {problems.map((p) => <div key={p}>{p}</div>)}
        </div>
      )}
      <div>
        <div style={{ ...CP_LABEL, marginBottom: 8 }}>Audience</div>
        <CpAudience c={c} industries={d && d.industries} pbOwner={d && d.pb_owner} />
      </div>
      <CpEstimates est={est} only={CP_COUNTS} />
      <CpSample rows={est.sample} />
      <CpRecentSales rows={est.recent_sales} />
      <div>
        <div style={{ ...CP_LABEL, marginBottom: 8 }}>Expected</div>
        <CpEstimates est={est} skip={CP_COUNTS} />
      </div>
      {c.rationale && (
        <div>
          <div style={{ ...CP_LABEL, marginBottom: 4 }}>Why</div>
          <window.Markdown style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55 }}>{c.rationale}</window.Markdown>
        </div>
      )}
      <div>
        <div style={{ ...CP_LABEL, marginBottom: 8 }}>Copy</div>
        {!d ? <window.SkeletonRows rows={3} />
          : d.error ? <window.Pill hue="rose">{d.error}</window.Pill>
          : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
              {d.variants.map((v) => <CpVariant key={v.id} v={v} />)}
            </div>}
      </div>
    </div>
  );
};

// ── Running / done rows ──────────────────────────────────────────────────────

const CpCampaignRow = ({ c, onOpen }) => (
  <button type="button" data-campaign-row={c.id} onClick={() => onOpen(c.id)}
    style={{ ...CP_CARD, padding: '12px 14px', textAlign: 'left', cursor: 'pointer', display: 'grid', gap: 8, width: '100%', fontFamily: 'inherit', color: 'var(--text)' }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13.5, fontWeight: 600 }}>{c.name}</span>
      <CpStatus c={c} />
      <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text-4)' }}>
        {c.status === 'done' || c.status === 'stopped'
          ? `${cpWhen(c.ended_at)}${c.end_reason ? ` · ${c.end_reason}` : ''}`
          : `${cpNum(c.daily_target)}/day · since ${cpWhen(c.started_at)}`}
      </span>
    </div>
    <CpFunnel f={c.funnel || {}} compact shadow={cpShadow(c)} />
  </button>
);

// ── One campaign ─────────────────────────────────────────────────────────────

const CpWouldSendEmails = ({ emails, claims }) => {
  const [open, setOpen] = React.useState(null);
  const marks = (step, part) => claims.filter((c) => c.step === step && c.part === part);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {emails.map((e) => {
        const pick = (c) => setOpen((o) => (o && cpClaimKey(o) === cpClaimKey(c) ? null : c));
        return (
          <div key={e.step} style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10 }}>
            <div style={{ fontSize: 11.5, color: 'var(--text-4)' }} title="The plan's days; the provider campaign's own waits decide at send">
              Email {e.step} · day {e.day} · {cpDate(e.date)}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}><CpMarked text={e.subject} marks={marks(e.step, 'subject')} onPick={pick} /></div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'pre-wrap', marginTop: 3, lineHeight: 1.5 }}>
              <CpMarked text={e.body} marks={marks(e.step, 'body')} onPick={pick} />
            </div>
            {open && open.step === e.step && <CpClaimCard c={open} onClose={() => setOpen(null)} />}
          </div>
        );
      })}
    </div>
  );
};

// Shadow: the emails this owner would get, as the pre-send check saw them.
const CpWouldSend = ({ w, leadId }) => (
  <div data-would-send>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
      <span style={CP_LABEL}>Would send</span>
      {w.variant && <window.Pill hue="violet" sz="sm">{w.variant}</window.Pill>}
      {w.side && <window.Pill hue="slate" sz="sm" title="Inbox side the push would route this address to">{w.side === 'filtered' ? 'Filtered inbox' : 'Clean inbox'}</window.Pill>}
      {w.skipped && <window.Pill hue="slate" sz="sm">Skipped: {w.skipped}</window.Pill>}
      {w.held && w.held.length > 0 && <window.Pill hue="amber" sz="sm">Held</window.Pill>}
    </div>
    {w.held && w.held.length > 0 && (
      <div style={{ display: 'grid', gap: 4, marginBottom: 8 }}>
        {w.held.map((h, i) => <div key={i} style={{ fontSize: 12.5, color: 'var(--amber-fg)' }}>{h}</div>)}
      </div>
    )}
    <CpWouldSendEmails emails={w.emails || []} claims={w.claims || []} />
    {leadId && (w.emails || []).length > 0 && <div style={{ marginTop: 10 }}><CpSendTest leadId={leadId} /></div>}
  </div>
);

const CpLeadWhy = ({ leadId, onClose }) => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    setD(null);
    cpGet(`/leads/${leadId}`).then((x) => { if (live) setD(x); }).catch((e) => { if (live) setD({ error: e.message }); });
    return () => { live = false; };
  }, [leadId]);
  const mark = (ok) => (ok === true ? ['✓', 'var(--green-fg, #2f855a)'] : ok === false ? ['✗', 'var(--rose-fg)'] : ['?', 'var(--text-4)']);
  const q = d && d.ai && d.ai.qualify;
  const own = d && d.ai && d.ai.ownership;
  return (
    <window.Drawer open onClose={onClose} width={460} zIndex={60}
      title={d && !d.error ? d.company.name : 'Lead'}
      subtitle={d && !d.error ? `${d.contact.name}${d.contact.title ? ` · ${d.contact.title}` : ''}` : null}>
      {!d ? <window.SkeletonRows rows={5} /> : d.error ? <window.Pill hue="rose">{d.error}</window.Pill> : (
        <div style={{ display: 'grid', gap: 18 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <window.Pill hue={(CP_LEAD_STATUS[d.status] || [])[1] || 'slate'} sz="sm">{(CP_LEAD_STATUS[d.status] || [d.status])[0]}</window.Pill>
            {d.variant && <window.Pill hue="violet" sz="sm">{d.variant}</window.Pill>}
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{d.email}</span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, fontSize: 12 }}>
              <a href={`#/companies/${d.company.id}`} style={{ color: 'var(--accent-text)' }}>Company</a>
              <a href={`#/people/${d.contact.id}`} style={{ color: 'var(--accent-text)' }}>Person</a>
            </span>
          </div>
          {d.detail && <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{d.detail}</div>}
          <div>
            <div style={{ ...CP_LABEL, marginBottom: 8 }}>Checks</div>
            {!d.reasons.length && <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Not checked yet.</div>}
            <div style={{ display: 'grid', gap: 6 }}>
              {d.reasons.map((r, i) => {
                const [glyph, color] = mark(r.ok);
                return (
                  <div key={i} data-check={r.check} style={{ display: 'grid', gridTemplateColumns: '16px 90px 1fr', gap: 8, fontSize: 12.5 }}>
                    <span style={{ color, fontWeight: 700 }}>{glyph}</span>
                    <span style={{ color: 'var(--text-3)' }}>{cpHuman(r.check)}</span>
                    <span style={{ color: 'var(--text)' }}>{r.detail}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {d.would_send && <CpWouldSend w={d.would_send} leadId={d.id} />}
          {(q || own) && (
            <div>
              <div style={{ ...CP_LABEL, marginBottom: 8 }}>Site read</div>
              <div style={{ display: 'grid', gap: 6, fontSize: 12.5 }}>
                {q && q.qualification_reason && <div>{q.qualification_reason}</div>}
                {q && q.size_detail && <div style={{ color: 'var(--text-3)' }}>{q.size_detail}</div>}
                {q && q.geo_reason && <div style={{ color: 'var(--text-3)' }}>{q.geo_reason}</div>}
                {own && <div>Ownership: {cpHuman(own.final_outcome || 'unknown')}{own.final_reason ? ` — ${own.final_reason}` : ''}</div>}
              </div>
            </div>
          )}
          <div>
            <div style={{ ...CP_LABEL, marginBottom: 8 }}>Since picked</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {[['picked_at', 'Picked'], ['qualified_at', 'Qualified'], ['enrolled_at', 'Enrolled'], ['ended_at', 'Ended']]
                .filter(([k]) => d[k]).map(([k, label]) => (
                  <div key={k} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10, fontSize: 12.5 }}>
                    <span style={{ color: 'var(--text-4)' }}>{cpWhen(d[k])}</span><span>{label}</span>
                  </div>
                ))}
              {d.events.slice().reverse().map((e, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10, fontSize: 12.5 }}>
                  <span style={{ color: 'var(--text-4)' }}>{cpWhen(e.occurred_at)}</span>
                  <span>
                    {e.event_type === 'sent' ? `Email ${e.sequence_step || ''} sent`
                      : e.event_type === 'status_change' ? `Status → ${cpHuman(e.to_status || '')}`
                      : cpHuman(e.event_type)}
                    {e.interest && <span style={{ color: 'var(--text-3)' }}> · {cpHuman(e.interest)}</span>}
                    {e.text && <div style={{ color: 'var(--text-2)', whiteSpace: 'pre-wrap', marginTop: 2 }}>{e.text}</div>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </window.Drawer>
  );
};

const CpLeads = ({ campaignId, counts }) => {
  const [status, setStatus] = React.useState('');
  const [rows, setRows] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    setRows(null);
    cpGet(`/campaigns/${campaignId}/leads?limit=300${status ? `&status=${status}` : ''}`)
      .then((x) => { if (live) setRows(x.leads || []); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [campaignId, status]);
  const total = Object.values(counts || {}).reduce((s, n) => s + n, 0);
  const chips = [['', 'All', total], ...Object.keys(CP_LEAD_STATUS).filter((k) => (counts || {})[k]).map((k) => [k, CP_LEAD_STATUS[k][0], counts[k]])];
  const table = rows && rows.map((r) => ({
    __id: r.id, company: r.company, person: cpPerson(r),
    status: (CP_LEAD_STATUS[r.status] || [r.status])[0], variant: r.variant || '', why: r.detail || '', picked: cpWhen(r.picked_at),
  }));
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {chips.map(([k, label, n]) => (
          <window.Btn key={k || 'all'} size="sm" variant={status === k ? 'outline' : 'ghost'} active={status === k} onClick={() => setStatus(k)}>
            {label} <span style={{ color: 'var(--text-4)' }}>{cpNum(n)}</span>
          </window.Btn>
        ))}
      </div>
      {!rows ? <window.SkeletonRows rows={6} /> : (
        <window.DataTableWidget rows={table} hideIds pageSize={50} maxHeight={520}
          columns={[{ key: 'company', label: 'Company' }, { key: 'person', label: 'Person' }, { key: 'status', label: 'Status' },
                    { key: 'variant', label: 'Variant' }, { key: 'why', label: 'Why' }, { key: 'picked', label: 'Picked' }]}
          onRowClick={(r) => setOpen(r.__id)} />
      )}
      {open && <CpLeadWhy leadId={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const CpReplies = ({ campaignId }) => {
  const [rows, setRows] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    cpGet(`/campaigns/${campaignId}/replies`).then((x) => { if (live) setRows(x.replies || []); }).catch(() => { if (live) setRows([]); });
    return () => { live = false; };
  }, [campaignId]);
  if (!rows) return <window.SkeletonRows rows={4} />;
  if (!rows.length) return <div style={{ fontSize: 12.5, color: 'var(--text-4)', padding: '8px 0' }}>No replies yet.</div>;
  const hue = (i) => (['interested', 'meeting_request'].includes(i) ? 'green' : i === 'not_interested' ? 'rose' : 'slate');
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {rows.map((r) => (
        <div key={r.id} data-reply={r.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
            <a href={`#/people/${r.contact_id}`} style={{ fontWeight: 600, color: 'var(--text)' }}>{cpPerson(r)}</a>
            <a href={`#/companies/${r.company_id}`} style={{ color: 'var(--accent-text)' }}>{r.company}</a>
            <span style={{ color: 'var(--text-4)' }}>{[r.hq_city, r.hq_state].filter(Boolean).join(', ')}</span>
            {r.interest && <window.Pill hue={hue(r.interest)} sz="sm">{cpHuman(r.interest)}</window.Pill>}
            {r.variant && <window.Pill hue="violet" sz="sm">{r.variant}</window.Pill>}
            <span style={{ marginLeft: 'auto', color: 'var(--text-4)', fontSize: 11.5 }}>
              {r.assigned_to ? `${r.assigned_to} · ` : r.card_status ? `${cpHuman(r.card_status)} · ` : ''}{cpWhen(r.occurred_at)}
            </span>
          </div>
          {r.text && <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{r.text}</div>}
        </div>
      ))}
    </div>
  );
};

// A lead's emails (or a variant's, for one of its owners) to typed addresses,
// from Atlas's mailbox with subjects marked [Test n/N]. The last address used
// is remembered on this browser.
const CP_TEST_TO_KEY = 'atlas.emailCopilot.testTo';
const cpSavedTestTo = () => { try { return localStorage.getItem(CP_TEST_TO_KEY) || ''; } catch (e) { return ''; } };
const CpSendTest = ({ leadId, variantId }) => {
  const [open, setOpen] = React.useState(false);
  const [to, setTo] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  window.useEscapeKey(open && !busy, () => { setOpen(false); setErr(null); });
  if (!open) {
    return (
      <window.Btn size="sm" variant="outline" onClick={() => { setTo((t) => t || cpSavedTestTo()); setOpen(true); }}
                  title="Email these to an address you type, from Atlas, subjects marked [Test]">Send test</window.Btn>
    );
  }
  const send = async () => {
    if (busy || !to.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await cpSend('/test-email', 'POST', { to: to.trim(), ...(leadId ? { lead_id: leadId } : { variant_id: variantId }) });
      try { localStorage.setItem(CP_TEST_TO_KEY, to.trim()); } catch (e) { /* private window */ }
      window.pushToast({ type: 'success', title: `Sent ${cpPlural(r.sent, 'test email')} to ${r.to.join(', ')}`, body: r.owner ? `As ${r.owner} would get them` : '' });
      setOpen(false);
    } catch (e) { setErr((e && e.message) || "Couldn't send it"); }
    finally { setBusy(false); }
  };
  return (
    <div data-send-test style={{ display: 'grid', gap: 6, width: '100%' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <window.TextInput autoFocus type="text" inputMode="email" autoComplete="email" aria-label="Send the test to"
                          value={to} onChange={(e) => { setTo(e.target.value); setErr(null); }}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
                          placeholder="name@industrypro.com" style={{ flex: '1 1 220px', width: 'auto', minWidth: 0 }} />
        <window.Btn size="sm" variant="accent" disabled={busy || !to.trim()} onClick={send}>{busy ? 'Sending…' : 'Send'}</window.Btn>
        <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setErr(null); }}>Cancel</window.Btn>
      </div>
      {err && <div role="alert" style={{ fontSize: 12, color: 'var(--rose-fg)' }}>{err}</div>}
    </div>
  );
};

// A variant copied from a SuperSend campaign (its current copy). In shadow the
// owners' previews split again across the variants.
const CpAddVariant = ({ campaignId, onAdded }) => {
  const [open, setOpen] = React.useState(false);
  const [label, setLabel] = React.useState('Current copy');
  const [ssId, setSsId] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  if (!open) {
    return <div><window.Btn size="sm" variant="outline" onClick={() => setOpen(true)}>Add variant</window.Btn></div>;
  }
  const add = async () => {
    setBusy(true); setErr(null);
    try {
      await cpSend(`/campaigns/${campaignId}/variants`, 'POST', { label: label.trim(), supersend_campaign_id: ssId.trim() });
      window.pushToast({ type: 'success', title: `Variant added — ${label.trim()}` });
      setOpen(false); setSsId('');
      onAdded();
    } catch (e) { setErr((e && e.message) || "Couldn't add it"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 8, padding: 12, display: 'grid', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 10 }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={CP_LABEL}>Label</span>
          <window.TextInput value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span style={CP_LABEL}>SuperSend campaign ID</span>
          <window.TextInput value={ssId} onChange={(e) => setSsId(e.target.value)} placeholder="Copies its emails as this variant" />
        </label>
      </div>
      {err && <window.Pill hue="rose">{err}</window.Pill>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setErr(null); }}>Cancel</window.Btn>
        <window.Btn size="sm" variant="accent" disabled={busy || !label.trim() || ssId.trim().length < 8} onClick={add}>
          {busy ? 'Adding…' : 'Add'}
        </window.Btn>
      </div>
    </div>
  );
};

const CpCampaignDrawer = ({ id, onClose, onChanged, onAsk }) => {
  const [d, setD] = React.useState(null);
  const [tab, setTab] = React.useState('progress');
  const [busy, setBusy] = React.useState(null);
  const load = React.useCallback(() => cpGet(`/campaigns/${id}`).then(setD).catch((e) => setD({ error: e.message })), [id]);
  React.useEffect(() => { setD(null); setTab('progress'); load(); }, [id, load]);
  const c = d && d.campaign;
  const act = async (action) => {
    if (!(await cpConfirm(action, c))) return;
    setBusy(action);
    try {
      await cpSend(`/campaigns/${c.id}/${action}`, 'POST', action === 'stop' ? { reason: 'stopped by hand' } : null);
      window.pushToast({ type: 'success', title: { live: 'Live', pause: 'Paused', resume: 'Resumed', stop: 'Stopped' }[action] + ` — ${c.name}` });
      await load();
      onChanged();
    } catch (e) { cpToastError("Couldn't do that", e); }
    finally { setBusy(null); }
  };
  const footer = c && (
    <div style={{ display: 'flex', gap: 6, width: '100%', alignItems: 'center' }}>
      <window.Btn size="sm" variant="ghost" onClick={() => onAsk(c, `About “${c.name}”: `)}>Ask about this</window.Btn>
      <span style={{ flex: 1 }} />
      {(c.status === 'running' || c.status === 'paused') && (
        <window.Btn size="sm" variant="ghost" disabled={!!busy} onClick={() => act('stop')}>Stop</window.Btn>
      )}
      {c.status === 'running' && <window.Btn size="sm" variant="outline" disabled={!!busy} onClick={() => act('pause')}>Pause</window.Btn>}
      {c.status === 'paused' && <window.Btn size="sm" variant="outline" disabled={!!busy} onClick={() => act('resume')}>Resume</window.Btn>}
      {(c.status === 'running' || c.status === 'paused') && c.mode !== 'live' && (
        <window.Btn size="sm" variant="accent" disabled={!!busy} onClick={() => act('live')}>{busy === 'live' ? 'Going live…' : 'Go live'}</window.Btn>
      )}
    </div>
  );
  const daily = (d && d.daily) || [];
  return (
    <window.Drawer open onClose={onClose} width={860}
      title={c ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>{c.name} <CpStatus c={c} /></span> : 'Campaign'}
      subtitle={c ? (c.end_reason ? `Ended ${cpWhen(c.ended_at)} · ${c.end_reason}` : `${cpNum(c.daily_target)}/day · since ${cpWhen(c.started_at)}`) : null}
      footer={footer}>
      {!d ? <window.SkeletonRows rows={8} /> : d.error ? <window.Pill hue="rose">{d.error}</window.Pill> : (
        <div style={{ display: 'grid', gap: 16 }}>
          <CpTabs tab={tab} onTab={setTab} tabs={[
            ['progress', 'Progress'], ['plan', 'Plan'], ['variants', 'Variants', d.variants.length],
            ['leads', 'Leads', c.funnel.picked], ['replies', 'Replies', c.funnel.replied], ['journal', 'Journal'],
          ]} />
          {tab === 'progress' && (
            <div style={{ display: 'grid', gap: 20 }}>
              <CpFunnel f={c.funnel} shadow={cpShadow(c)} />
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <CpStat label="Emails sent" value={cpNum(c.funnel.emails)} />
                <CpStat label="Reply rate" value={cpPct(c.funnel.reply_rate)} />
                <CpStat label="Positive rate" value={cpPct(c.funnel.positive_rate)} />
                <CpStat label="Said no" value={cpNum(c.funnel.said_no)} />
                <CpStat label="Bounced" value={cpNum(c.funnel.bounced)} />
              </div>
              {daily.some((x) => x.enrolled || x.emails || x.replied) && (
                <div>
                  <div style={{ ...CP_LABEL, marginBottom: 6 }}>Last {daily.length} days</div>
                  <window.BarChart height={220} categories={daily.map((x) => cpDate(x.day))}
                    series={[{ name: 'Enrolled', values: daily.map((x) => x.enrolled) },
                             { name: 'Emails', values: daily.map((x) => x.emails) },
                             { name: 'Replies', values: daily.map((x) => x.replied) }]} />
                </div>
              )}
            </div>
          )}
          {tab === 'plan' && (
            <div style={{ display: 'grid', gap: 16 }}>
              <CpAudience c={c} industries={d.industries} pbOwner={d.pb_owner} />
              <CpEstimates est={c.estimates} />
              <CpSample rows={(c.estimates || {}).sample} />
              <CpRecentSales rows={(c.estimates || {}).recent_sales} />
              {c.rationale && <window.Markdown style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55 }}>{c.rationale}</window.Markdown>}
            </div>
          )}
          {tab === 'variants' && (
            <div style={{ display: 'grid', gap: 12 }}>
              {d.variants.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginBottom: -6 }}>
                  <window.Btn size="xs" variant="ghost" onClick={() => cpFoldVariants(d.variants, true)}>Expand all</window.Btn>
                  <span style={{ color: 'var(--text-4)', fontSize: 11, alignSelf: 'center' }}>·</span>
                  <window.Btn size="xs" variant="ghost" onClick={() => cpFoldVariants(d.variants, false)}>Collapse all</window.Btn>
                </div>
              )}
              {cpVariantOrder(d.variants).map((v) => (
                <CpVariant key={v.id} v={v} showResults shadow={cpShadow(c)}
                  owners={d.variants.reduce((n, x) => n + ((x.results || {}).would_send || 0), 0)} />
              ))}
              {c.mode !== 'live' && ['draft', 'running', 'paused'].includes(c.status) && (
                <CpAddVariant campaignId={c.id} onAdded={() => { load(); onChanged(); }} />
              )}
            </div>
          )}
          {tab === 'leads' && <CpLeads campaignId={c.id} counts={c.counts} />}
          {tab === 'replies' && <CpReplies campaignId={c.id} />}
          {tab === 'journal' && <CpJournal rows={d.journal} />}
        </div>
      )}
    </window.Drawer>
  );
};

// ── Mailboxes ────────────────────────────────────────────────────────────────

// Who a mailbox sends as: the name in SuperSend's "Name <address>", else the
// address's own name part — what you pick boxes by.
const cpBoxPerson = (m) => {
  const raw = String(m.send_as || '').split('<')[0].trim();
  return raw || `${String(m.email || '').split('@')[0]}@`;
};
const CP_BOX_SHOW = {
  picked: ['Picked', (m, on) => on],
  unpicked: ['Not picked', (m, on) => !on && !m.excluded_reason],
  bouncing: ['Bouncing over 5%', (m) => m.bounce_rate > 0.05],
  blocked: ["Can't be used", (m) => !!m.excluded_reason],
};
// A row stays in view when it matches the search (address, domain, sender
// name), the Show filter and the Sends-as filter (pure).
const cpBoxShown = (m, { q, show, who }, on) => {
  const t = (q || '').trim().toLowerCase();
  if (t && ![m.email, m.domain, m.send_as].some((x) => String(x || '').toLowerCase().includes(t))) return false;
  if (show && CP_BOX_SHOW[show] && !CP_BOX_SHOW[show][1](m, on)) return false;
  return !who || cpBoxPerson(m) === who;
};

const CpMailboxes = ({ onChanged }) => {
  const [d, setD] = React.useState(null);
  const [picked, setPicked] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [f, setF] = React.useState({ q: '', show: '', who: '' });
  const load = React.useCallback(() => cpGet('/mailboxes').then((x) => { setD(x); setPicked(new Set(x.mailboxes.filter((m) => m.managed).map((m) => m.sender_id))); })
    .catch((e) => setD({ error: e.message, mailboxes: [] })), []);
  React.useEffect(() => { load(); }, [load]);
  if (!d) return <window.SkeletonRows rows={6} />;
  const boxes = d.mailboxes || [];
  const was = new Set(boxes.filter((m) => m.managed).map((m) => m.sender_id));
  const dirty = picked && (picked.size !== was.size || [...picked].some((x) => !was.has(x)));
  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const on = (m) => !!(picked && picked.has(m.sender_id));
  const narrowed = !!(f.q.trim() || f.show || f.who);
  // Saved picks first, then by sender and address: rows don't jump while you tick.
  const shown = boxes.filter((m) => cpBoxShown(m, f, on(m))).sort((a, b) =>
    (was.has(b.sender_id) - was.has(a.sender_id)) || cpBoxPerson(a).localeCompare(cpBoxPerson(b))
    || String(a.email).localeCompare(String(b.email)));
  const people = Object.entries(boxes.reduce((acc, m) => { const k = cpBoxPerson(m); acc[k] = (acc[k] || 0) + 1; return acc; }, {}))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([k, n]) => ({ value: k, label: k, count: n }));
  const showOpts = Object.entries(CP_BOX_SHOW).map(([k, [label, test]]) => ({ value: k, label, count: boxes.filter((m) => test(m, on(m))).length }));
  const pickShown = (add) => setPicked((s) => {
    const n = new Set(s);
    shown.forEach((m) => { if (add && !m.excluded_reason) n.add(m.sender_id); if (!add) n.delete(m.sender_id); });
    return n;
  });
  const save = async () => {
    setBusy('save');
    try {
      const x = await cpSend('/mailboxes', 'PUT', { managed: [...picked] });
      setD(x); setPicked(new Set(x.mailboxes.filter((m) => m.managed).map((m) => m.sender_id)));
      window.pushToast({ type: 'success', title: 'Mailboxes saved' });
      onChanged();
    } catch (e) { cpToastError("Couldn't save", e); }
    finally { setBusy(null); }
  };
  const sync = async () => {
    setBusy('sync');
    try { const x = await cpSend('/mailboxes/sync', 'POST'); setD(x); setPicked(new Set(x.mailboxes.filter((m) => m.managed).map((m) => m.sender_id))); }
    catch (e) { cpToastError("Couldn't read the mailboxes", e); }
    finally { setBusy(null); }
  };
  const day = (m) => (m.ramp_started_at ? Math.max(0, Math.floor((Date.now() - new Date(m.ramp_started_at)) / 86400000)) : null);
  const th = { ...CP_LABEL, textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const td = { padding: '7px 8px', borderBottom: '1px solid var(--border)', fontSize: 12.5, whiteSpace: 'nowrap' };
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          {cpPlural(picked ? picked.size : 0, 'mailbox', 'mailboxes')} managed · {cpNum(d.capacity)} emails/day
        </span>
        {d.error && <window.Pill hue="rose" sz="sm">{d.error}</window.Pill>}
        <span style={{ flex: 1 }} />
        <window.Btn size="sm" variant="ghost" disabled={!!busy} onClick={sync}>{busy === 'sync' ? 'Reading…' : 'Refresh from SuperSend'}</window.Btn>
        <window.Btn size="sm" variant="accent" disabled={!dirty || !!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</window.Btn>
      </div>
      {boxes.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', maxWidth: 320 }}>
            <window.TextInput type="search" value={f.q} placeholder="Search address, domain or sender" aria-label="Search mailboxes"
              onChange={(e) => setF({ ...f, q: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Escape' && f.q) { e.stopPropagation(); setF({ ...f, q: '' }); } }} />
          </div>
          <window.Picker value={f.show} options={showOpts} emptyLabel="All mailboxes" size="sm" title="Show"
            onChange={(v) => setF({ ...f, show: v || '' })} />
          <window.Picker value={f.who} options={people} emptyLabel="Any sender" size="sm" searchable title="Sends as"
            onChange={(v) => setF({ ...f, who: v || '' })} />
          {narrowed && (
            <>
              <span style={{ fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{cpNum(shown.length)} of {cpNum(boxes.length)}</span>
              <window.Btn size="sm" variant="ghost" disabled={!shown.some((m) => !m.excluded_reason && !on(m))}
                onClick={() => pickShown(true)} title="Tick every mailbox in this list">Pick all shown</window.Btn>
              <window.Btn size="sm" variant="ghost" disabled={!shown.some(on)} onClick={() => pickShown(false)}
                title="Untick every mailbox in this list">Clear shown</window.Btn>
              <window.Btn size="sm" variant="ghost" onClick={() => setF({ q: '', show: '', who: '' })}>Clear filters</window.Btn>
            </>
          )}
        </div>
      )}
      {!boxes.length ? <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>No mailboxes read yet.</div> : !shown.length ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-4)', padding: '12px 2px' }}>No mailboxes match.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr>
              <th style={th} /><th style={th}>Mailbox</th><th style={th}>Sends as</th><th style={th}>Status</th><th style={th}>Cap / day</th>
              <th style={th}>Ramp day</th><th style={th}>Sends (7d)</th><th style={th}>Bounce</th><th style={th}>Health</th><th style={th}>Read</th>
            </tr></thead>
            <tbody>
              {shown.map((m) => {
                const [label, hue] = CP_BOX_STATUS[m.status] || [m.status, 'slate'];
                const blocked = !!m.excluded_reason;
                return (
                  <tr key={m.sender_id} data-mailbox={m.sender_id} style={{ opacity: blocked ? 0.6 : 1 }}>
                    <td style={td}>
                      <input type="checkbox" checked={!!(picked && picked.has(m.sender_id))} disabled={blocked}
                        onChange={() => toggle(m.sender_id)} aria-label={`Manage ${m.email}`} />
                    </td>
                    <td style={td}>
                      <div>{m.email}</div>
                      {m.excluded_reason && <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{m.excluded_reason}</div>}
                    </td>
                    <td style={{ ...td, color: m.send_as ? 'var(--text-2)' : 'var(--text-4)' }}>{m.send_as ? cpBoxPerson(m) : '—'}</td>
                    <td style={td}><window.Pill hue={hue} sz="sm">{label}</window.Pill></td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{m.managed ? cpNum(m.cap) : cpNum(m.provider_cap)}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{m.managed && day(m) != null ? day(m) : '—'}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{cpNum((m.health || {}).sends)}</td>
                    <td style={{ ...td, color: m.bounce_rate > 0.05 ? 'var(--rose-fg)' : undefined }}>{cpPct(m.bounce_rate)}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{cpNum((m.health || {}).health_score)}</td>
                    <td style={{ ...td, color: 'var(--text-4)' }}>{cpWhen(m.checked_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Settings ─────────────────────────────────────────────────────────────────

const CpSettings = ({ onChanged }) => {
  const [s, setS] = React.useState(null);
  const [draft, setDraft] = React.useState(null);
  const [channels, setChannels] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    cpGet('/settings').then((x) => { setS(x.settings); setDraft(x.settings); }).catch((e) => setS({ error: e.message }));
    cpGet('/slack-channels').then(setChannels).catch((e) => setChannels({ channels: [], error: e.message }));
  }, []);
  if (!s) return <window.SkeletonRows rows={6} />;
  if (s.error) return <window.Pill hue="rose">{s.error}</window.Pill>;
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setFoot = (k, v) => setDraft((d) => ({ ...d, footer: { ...d.footer, [k]: v } }));
  const keys = ['footer', 'slack_channel', 'template_campaign_id', 'slots', 'auto_live', 'auto_propose', 'pause_bounce_rate'];
  const changed = keys.filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(s[k]));
  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      changed.forEach((k) => { body[k] = draft[k]; });
      const x = await cpSend('/settings', 'PUT', body);
      setS(x.settings); setDraft(x.settings);
      window.pushToast({ type: 'success', title: 'Settings saved' });
      onChanged();
    } catch (e) { cpToastError("Couldn't save", e); }
    finally { setSaving(false); }
  };
  const chOpts = ((channels && channels.channels) || []).map((c) => ({ value: c.id, label: `#${c.name}`, hint: c.is_member ? undefined : 'bot not in it' }));
  const cur = draft.slack_channel || null;
  if (cur && cur.id && !chOpts.some((o) => o.value === cur.id)) chOpts.unshift({ value: cur.id, label: `#${cur.name || cur.id}` });
  const field = (label, control, hint) => (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={CP_LABEL}>{label}</span>
      {control}
      {hint && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{hint}</span>}
    </label>
  );
  const input = (value, onChange, placeholder) => (
    <window.TextInput value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  );
  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 640 }}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Footer</div>
        {field('LinkedIn page', input(draft.footer.linkedin_url, (v) => setFoot('linkedin_url', v), 'https://www.linkedin.com/company/…'))}
        {field('Office address', input(draft.footer.address, (v) => setFoot('address', v)))}
        {field('Opt-out line', input(draft.footer.opt_out, (v) => setFoot('opt_out', v)))}
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Replies</div>
        {field('Slack channel',
          <window.Picker value={cur ? cur.id : ''} options={chOpts} emptyLabel="None" searchable
            onChange={(v) => { const o = ((channels && channels.channels) || []).find((c) => c.id === v); set('slack_channel', v ? { id: v, name: o ? o.name : (cur && cur.name) || v } : null); }} />,
          channels && channels.error)}
      </div>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Sending</div>
        {field('Template campaign', input(draft.template_campaign_id, (v) => set('template_campaign_id', v.trim() || null), 'SuperSend campaign id'))}
        {field('Slots',
          <textarea value={(draft.slots || []).join('\n')} rows={4} placeholder="One SuperSend campaign id per line"
            onChange={(e) => set('slots', e.target.value.split('\n').map((x) => x.trim()).filter(Boolean))}
            style={{ fontSize: 12.5, fontFamily: 'var(--font-mono)', padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 6,
                     background: 'var(--surface)', color: 'var(--text)', resize: 'vertical' }} />,
          `${cpPlural((draft.slots || []).length, 'slot')}`)}
        {field('Pause a mailbox above',
          <window.Picker value={String(draft.pause_bounce_rate)} size="md"
            options={[0.03, 0.04, 0.05, 0.06, 0.08].map((x) => ({ value: String(x), label: `${Math.round(x * 100)}% bounces` }))}
            onChange={(v) => set('pause_bounce_rate', Number(v))} />)}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={!!draft.auto_live} onChange={(e) => set('auto_live', e.target.checked)} />
          New campaigns go live on Accept
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
          title="Mondays before the brief: a plan for the best industry no campaign covers, while fewer than 2 plans wait. Nothing runs until you accept.">
          <input type="checkbox" checked={!!draft.auto_propose} onChange={(e) => set('auto_propose', e.target.checked)} />
          Propose plans on Mondays
        </label>
      </div>
      <div>
        <window.Btn variant="accent" size="sm" disabled={!changed.length || saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</window.Btn>
      </div>
      <CpMarketData />
    </div>
  );
};

// GF Data's multiples (app/market_multiples.py): what's imported, and the
// GFDataScraper CSV to import. Our own closings need nothing here.
const CpMarketData = () => {
  const [st, setSt] = React.useState(null);
  const [stat, setStat] = React.useState('mean');
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);
  const load = () => window.AtlasApi.fetch('/api/market-multiples/gfdata', { cacheMs: 0 }).then(setSt).catch((e) => setSt({ error: e.message }));
  React.useEffect(() => { load(); }, []);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const csv = await file.text();
      const x = await window.AtlasApi.fetch('/api/market-multiples/gfdata', { method: 'POST', body: JSON.stringify({ csv, stat }) });
      setSt(x.status);
      window.pushToast({ type: 'success', title: `GF Data imported — ${cpPlural(x.rows, 'figure')}, ${cpPlural(x.naics, 'NAICS code')}` });
    } catch (e) { cpToastError("Couldn't import that file", e); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  return (
    <div style={{ display: 'grid', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>Market data</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
        {!st ? '…' : st.error ? <window.Pill hue="rose">{st.error}</window.Pill>
          : st.rows ? `GF Data: ${cpPlural(st.rows, 'figure')} across ${cpPlural(st.naics, 'NAICS code')}${st.medians ? ` (${cpNum(st.medians)} medians)` : ''} · imported ${cpWhen(st.last_import)}`
          : 'GF Data: nothing imported yet'}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <window.Picker value={stat} size="md" options={[{ value: 'mean', label: 'File has means' }, { value: 'median', label: 'File has medians' }]}
          onChange={(v) => setStat(v)} />
        <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}
          title="A CSV from GFDataScraper (TEV ranges or by year); a Stat column in the file wins">
          {busy ? 'Importing…' : 'Import GF Data CSV'}
        </window.Btn>
        <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={(e) => upload(e.target.files && e.target.files[0])} />
      </div>
    </div>
  );
};

// ── The chat ─────────────────────────────────────────────────────────────────

// Answers link records as [Name](#company/<uuid>); route them through AtlasNav.
const CP_ENTITY_RE = /^#(company|contact|engagement|deal)\/([0-9a-f-]{36})$/i;
const CP_ENTITY_NAV = { company: 'goToCompany', contact: 'goToContact', engagement: 'goToDeal', deal: 'goToDeal' };
const cpOnLink = (e) => {
  const a = e.target && e.target.closest ? e.target.closest("a[href^='#']") : null;
  const m = a && CP_ENTITY_RE.exec(a.getAttribute('href') || '');
  if (!m) return;
  e.preventDefault();
  const nav = window.AtlasNav && window.AtlasNav[CP_ENTITY_NAV[m[1].toLowerCase()]];
  if (nav) nav(m[2], a.textContent || undefined);
};

const CP_STARTERS = ['How did yesterday go?', 'Which variant is winning?', 'Why were leads skipped?', 'Plan a new campaign: '];

const CpChat = ({ campaign, seed, onClose, onAnswered }) => {
  const [messages, setMessages] = React.useState([]);
  const [conv, setConv] = React.useState(null);
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [error, setError] = React.useState('');
  const bottom = React.useRef(null);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (seed && seed.text != null) { setInput(seed.text); setTimeout(() => inputRef.current && inputRef.current.focus(), 0); }
  }, [seed]);
  React.useEffect(() => { if (bottom.current) bottom.current.scrollIntoView({ block: 'end' }); }, [messages, sending]);
  const send = async (text) => {
    const t = (text || '').trim();
    if (!t || sending) return;
    setError(''); setSending(true); setStatus('Thinking…'); setInput('');
    setMessages((ms) => [...ms, { role: 'user', content: t }]);
    try {
      const r = await window.AtlasApi.assistant.chatStream(
        { message: t, conversation_id: conv || undefined,
          context: { page: 'email_copilot', ...(campaign ? { campaign_id: campaign.id } : {}) } },
        (label) => setStatus(label || 'Thinking…'));
      setConv(r.conversation_id);
      setMessages((ms) => [...ms, r.message]);
      onAnswered();
    } catch (e) { setError((e && e.message) || 'The assistant is unavailable right now.'); }
    finally { setSending(false); setStatus(''); }
  };
  return (
    <aside data-copilot-chat style={{ width: 400, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column',
                                       borderLeft: '1px solid var(--border)', background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
        <window.Icon.Sparkle size={14} />
        <span style={{ fontSize: 13.5, fontWeight: 600 }}>Ask Copilot</span>
        {campaign && <window.Pill hue="slate" sz="sm" maxW={150}>{campaign.name}</window.Pill>}
        <span style={{ flex: 1 }} />
        {messages.length > 0 && <window.Btn size="sm" variant="ghost" onClick={() => { setMessages([]); setConv(null); setError(''); }}>New chat</window.Btn>}
        <window.Btn size="sm" variant="ghost" onClick={onClose} title="Close"><window.Icon.Close size={13} /></window.Btn>
      </div>
      <div onClick={cpOnLink} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!messages.length && !sending && (
          <div style={{ display: 'grid', gap: 6 }}>
            {CP_STARTERS.map((x) => (
              <button key={x} type="button" onClick={() => (x.endsWith(': ') ? (setInput(x), inputRef.current && inputRef.current.focus()) : send(x))}
                style={{ textAlign: 'left', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2, var(--surface))',
                         color: 'var(--text-2)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                {x.replace(/: $/, '…')}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (m.role === 'user'
          ? <div key={i} style={{ alignSelf: 'flex-end', maxWidth: '88%', background: 'var(--surface-hover)', borderRadius: 10, padding: '7px 10px', fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.content}</div>
          : <window.Markdown key={m.id || i} style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text)' }}>{m.content}</window.Markdown>))}
        {sending && <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{status || 'Thinking…'}</div>}
        {error && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>{error}</div>}
        <div ref={bottom} />
      </div>
      <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea ref={inputRef} value={input} rows={2} disabled={sending}
          placeholder={sending ? 'Working…' : 'Ask about results, leads, copy — or for a new plan'}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
          style={{ flex: 1, resize: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface-2, var(--surface))',
                   border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', outline: 'none' }} />
        <window.Btn size="sm" variant="accent" disabled={sending || !input.trim()} onClick={() => send(input)}>Send</window.Btn>
      </div>
    </aside>
  );
};

// ── The page ─────────────────────────────────────────────────────────────────

const EmailCopilotView = ({ currentRole }) => {
  const isAdmin = (currentRole || '').toLowerCase() === 'admin';
  const [ov, setOv] = React.useState(null);
  const [list, setList] = React.useState(null);
  const [tab, setTab] = React.useState('campaigns');
  const [win, setWin] = React.useState('today');
  const [openId, setOpenId] = React.useState(null);
  const [chat, setChat] = React.useState(null);     // {campaign, seed} while open
  const [busy, setBusy] = React.useState(false);
  // Eight KPI tiles in one row when there's room, two rows of four when not.
  const [kpiEl, setKpiEl] = React.useState(null);
  const [kpiW, setKpiW] = React.useState(0);
  React.useEffect(() => {
    if (!kpiEl || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setKpiW(e.contentRect.width));
    ro.observe(kpiEl);
    return () => ro.disconnect();
  }, [kpiEl]);

  const load = React.useCallback(() => Promise.all([cpGet('/overview'), cpGet('/campaigns')]).then(([o, l]) => {
    setOv(o); setList(l); return [o.kpis, o.paused, l.campaigns.map((c) => [c.id, c.status, c.mode, c.version, c.funnel])];
  }).catch((e) => { setOv((x) => x || { error: e.message }); throw e; }), []);
  React.useEffect(() => { if (isAdmin) load().catch(() => {}); }, [isAdmin, load]);
  window.usePolling(load, { active: isAdmin, first: 60000, giveUpAfter: 0 });

  const wrap = (children) => (
    <div style={{ height: '100%', display: 'flex', minWidth: 0 }}>
      <div style={{ flex: 1, minWidth: 0, height: '100%', overflowY: 'auto' }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 24px 48px', display: 'grid', gap: 18 }}>{children}</div>
      </div>
      {chat && <CpChat campaign={chat.campaign} seed={chat.seed} onClose={() => setChat(null)} onAnswered={() => load().catch(() => {})} />}
    </div>
  );
  if (!isAdmin) return wrap(<div style={{ fontSize: 13, color: 'var(--text-3)' }}>Admins only.</div>);
  if (!ov || !list) return wrap(<window.SkeletonRows rows={8} />);
  if (ov.error) return wrap(<window.Pill hue="rose">{ov.error}</window.Pill>);

  const ask = (campaign, text) => setChat({ campaign: campaign || null, seed: { text, at: Date.now() } });
  const pauseAll = async () => {
    const action = ov.paused ? 'resume-all' : 'pause-all';
    if (!(await cpConfirm(action))) return;
    setBusy(true);
    try { setOv(await cpSend(`/${action}`, 'POST')); await load(); }
    catch (e) { cpToastError("Couldn't do that", e); }
    finally { setBusy(false); }
  };
  const camps = list.campaigns || [];
  const drafts = camps.filter((c) => c.status === 'draft');
  const active = camps.filter((c) => c.status === 'running' || c.status === 'paused');
  const ended = camps.filter((c) => c.status === 'done' || c.status === 'stopped');
  const k = (ov.kpis || {})[win] || {};
  const r = ov.ramp || {};
  const running = (ov.campaigns || {}).running || 0;
  const rampLine = r.boxes ? [
    r.day != null ? `Ramp day ${r.day} · ${r.per_box}/day per box${r.next ? ` → ${r.next.per_box} on day ${r.next.day}` : ''}` : 'Ramp not started',
    `${cpPlural(r.boxes, 'mailbox', 'mailboxes')}${Object.keys(r.by_status || {}).length
      ? ` (${Object.entries(r.by_status).map(([s, n]) => `${n} ${((CP_BOX_STATUS[s] || [s])[0]).toLowerCase()}`).join(', ')})` : ''}`,
    `${cpNum(r.emails_per_day)} emails/day`,
    `~${cpNum(r.new_per_day)} new owners/day`,
  ] : ['No mailboxes managed yet'];

  return wrap(<>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)' }}>Email Copilot</h1>
      {ov.paused ? <window.Pill hue="amber" dot>Paused</window.Pill>
        : running ? <window.Pill hue="green" dot>Running</window.Pill>
        : <window.Pill hue="slate" dot>Idle</window.Pill>}
      {ov.last_tick_at && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>checked {cpWhen(ov.last_tick_at)}</span>}
      <span style={{ flex: 1 }} />
      <window.Btn size="sm" variant="outline" disabled={busy || (!ov.paused && !running)} onClick={pauseAll}>{ov.paused ? 'Resume' : 'Pause all'}</window.Btn>
      <window.Btn size="sm" variant="outline" onClick={() => ask(null, 'Plan a new campaign: ')}>New campaign</window.Btn>
      {!chat && <window.Btn size="sm" variant="accent" icon={<window.Icon.Sparkle size={12} />} onClick={() => ask(null, '')}>Ask Copilot</window.Btn>}
    </div>

    <div data-copilot-brief style={CP_CARD}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={CP_LABEL}>Today's brief</span>
        {ov.brief && <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{cpWhen(ov.brief.created_at)}</span>}
      </div>
      {ov.brief ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{ov.brief.summary}</div>
          {ov.brief.reasoning && <window.Markdown style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.55, marginTop: 6 }}>{ov.brief.reasoning}</window.Markdown>}
        </div>
      ) : <div style={{ fontSize: 12.5, color: 'var(--text-4)', marginTop: 6 }}>No brief yet.</div>}
    </div>

    <div style={{ fontSize: 12.5, color: 'var(--text-3)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {rampLine.map((x, i) => <span key={i}>{i > 0 && <span style={{ color: 'var(--text-4)', marginRight: 6 }}>·</span>}{x}</span>)}
    </div>

    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[['today', 'Today'], ['week', 'Last 7 days']].map(([w, label]) => (
          <window.Btn key={w} size="sm" variant={win === w ? 'outline' : 'ghost'} active={win === w} onClick={() => setWin(w)}>{label}</window.Btn>
        ))}
      </div>
      <div data-copilot-kpis ref={setKpiEl} style={{ display: 'grid', gridTemplateColumns: `repeat(${kpiW >= 960 ? 8 : 4}, minmax(0, 1fr))`, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
        {CP_KPIS.map(([key, label]) => (
          <div key={key} style={{ boxShadow: '1px 0 0 var(--border), 0 1px 0 var(--border)' }}>
            <window.KpiCard value={k[key] || 0} label={label} />
          </div>
        ))}
      </div>
    </div>

    <CpTabs tab={tab} onTab={setTab} tabs={[['campaigns', 'Campaigns', camps.length], ['mailboxes', 'Mailboxes', r.boxes], ['settings', 'Settings']]} />

    {tab === 'campaigns' && (
      <div style={{ display: 'grid', gap: 18 }}>
        {drafts.length > 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={CP_LABEL}>Proposed</div>
            {drafts.map((c) => <CpPlanCard key={c.id} c={c} onChanged={() => load().catch(() => {})} onAsk={ask} />)}
          </div>
        )}
        <div style={{ display: 'grid', gap: 10 }}>
          <div style={CP_LABEL}>Running</div>
          {active.length ? active.map((c) => <CpCampaignRow key={c.id} c={c} onOpen={setOpenId} />)
            : <div style={{ fontSize: 12.5, color: 'var(--text-4)' }}>Nothing running.</div>}
        </div>
        {ended.length > 0 && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={CP_LABEL}>Done</div>
            {ended.map((c) => <CpCampaignRow key={c.id} c={c} onOpen={setOpenId} />)}
          </div>
        )}
      </div>
    )}
    {tab === 'mailboxes' && <CpMailboxes onChanged={() => load().catch(() => {})} />}
    {tab === 'settings' && <CpSettings onChanged={() => load().catch(() => {})} />}

    {openId && <CpCampaignDrawer id={openId} onClose={() => setOpenId(null)} onChanged={() => load().catch(() => {})}
      onAsk={(c, text) => { setOpenId(null); ask(c, text); }} />}
  </>);
};

window.EmailCopilotView = EmailCopilotView;
